/**
 * Signed-cookie sessions for the dashboard.
 *
 * Request-edge code: it reads the cookie off the incoming `Request`, verifies
 * the HMAC and checks the caller's role. It touches no database and knows
 * nothing about the CMS, which is why it lives in a package rather than in
 * `apps/api-cms` where it started. A session check has to run wherever the
 * request first lands, so keeping it here means that as api-cms moves behind
 * its own deployment boundary this stays in-process at the edge instead of
 * turning every authenticated request into an extra network hop.
 *
 * Server-only — it reads `process.env` and the session secret. The
 * client-facing pieces are in `@playbrain/auth/dashboard`.
 */
import { isDevStage, unixMsDate, utcUnixMs } from "@dashboard-config"
import type { AuthUser, DashboardRole } from "../dashboardRoles"
import { hasDashboardRole } from "../dashboardRoles"

export const DASHBOARD_SESSION_COOKIE = "playbrain_dashboard_session"

/**
 * How long a session lasts, fixed at login.
 *
 * One lifetime, not a sliding idle window. The previous model expired a
 * session after eight hours of inactivity *and* set the cookie to live only
 * eight hours — so an overnight gap, which is reliably longer than that,
 * logged everyone out every morning, and the seven-day ceiling it also
 * declared could never be reached.
 *
 * Every comparable CMS does it this way: WordPress ships 48 hours (14 days
 * with "remember me"), Strapi a 30-day token, Ghost longer still, and none of
 * them idle-times an admin session out. A predictable expiry beats a clever
 * one, so this does not slide with use either — you are signed out on a
 * schedule you could write down, never for having gone home.
 */
export const DASHBOARD_SESSION_LIFETIME_MS = 14 * 24 * 60 * 60 * 1000
/** The "remember me" lifetime: a deliberate, user-made trade. */
export const DASHBOARD_SESSION_REMEMBERED_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000

export type DashboardSessionOptions = { rememberMe?: boolean }

type DashboardSessionClaims = {
  user: AuthUser
  createdAt: number
  /**
   * Last request this session was seen on. Kept for the "last active" column
   * and for support questions — it is no longer a gate.
   */
  lastSeenAt: number
  expiresAt: number
}

export type DashboardSession = DashboardSessionClaims & {
  token: string
}

export type DashboardAuthResult = { user: AuthUser; session: DashboardSession } | { response: Response }

export async function createDashboardSessionCookie(
  user: AuthUser,
  options: DashboardSessionOptions = {},
  now = utcUnixMs()
) {
  const claims = createDashboardSessionClaims(user, options, now)
  const token = await signDashboardSession(claims)
  return serializeDashboardSessionCookie(token, claims.expiresAt)
}

export async function readDashboardSession(request: Request, now = utcUnixMs()) {
  const cookie = parseCookieHeader(request.headers.get("cookie"))
  const token = cookie[DASHBOARD_SESSION_COOKIE]
  if (!token) return null

  const claims = await verifyDashboardSession(token)
  if (!claims) return null
  // One check, because there is one deadline. Revocation is enforced a layer
  // up, where the user record is already loaded.
  if (claims.expiresAt <= now) return null

  return { ...claims, token } satisfies DashboardSession
}

/**
 * Re-issues the cookie without moving its deadline.
 *
 * The expiry is fixed at login, so this only refreshes `lastSeenAt` and hands
 * the browser the same deadline again. It exists so the cookie stays present
 * and its activity stamp stays current — not to extend anything.
 */
export async function refreshDashboardSessionCookie(session: DashboardSession, now = utcUnixMs()) {
  const claims: DashboardSessionClaims = {
    user: session.user,
    createdAt: session.createdAt,
    lastSeenAt: now,
    expiresAt: session.expiresAt,
  }
  const token = await signDashboardSession(claims)
  return serializeDashboardSessionCookie(token, session.expiresAt)
}

export function clearDashboardSessionCookie() {
  return serializeCookie(DASHBOARD_SESSION_COOKIE, "", {
    maxAge: 0,
    expires: unixMsDate(0),
    path: "/",
    httpOnly: true,
    secure: shouldUseSecureCookies(),
    sameSite: "Lax",
  })
}

export async function requireDashboardCookieAuth(
  request: Request,
  roles: DashboardRole[] = ["admin", "editor", "viewer"]
): Promise<DashboardAuthResult> {
  const session = await readDashboardSession(request)
  if (!session) {
    if (allowLocalDevBypass()) {
      const now = utcUnixMs()
      return {
        user: {
          sub: "local-dev",
          dashboardLanguage: "en",
          email: "local-dev@playbrain.com",
          name: "Local Dev",
          username: "Local Dev",
          role: "admin",
          roles: ["admin"],
        },
        session: {
          token: "local-dev",
          user: {
            sub: "local-dev",
            dashboardLanguage: "en",
            email: "local-dev@playbrain.com",
            name: "Local Dev",
            username: "Local Dev",
            role: "admin",
            roles: ["admin"],
          },
          createdAt: now,
          lastSeenAt: now,
          expiresAt: now + DASHBOARD_SESSION_LIFETIME_MS,
        },
      }
    }
    return { response: Response.json({ error: "Missing dashboard session" }, { status: 401 }) }
  }

  if (roles.length > 0 && !hasDashboardRole(session.user, roles)) {
    return { response: Response.json({ error: "Insufficient dashboard role" }, { status: 403 }) }
  }

  return { user: session.user, session }
}

export function parseCookieHeader(header: string | null): Record<string, string> {
  const cookies: Record<string, string> = {}
  if (!header) return cookies
  for (const part of header.split(";")) {
    const [name, ...valueParts] = part.trim().split("=")
    if (!name) continue
    cookies[name] = decodeURIComponent(valueParts.join("="))
  }
  return cookies
}

export function dashboardSessionPublicPayload(session: DashboardSession) {
  return {
    user: session.user,
    expiresAt: session.expiresAt,
  }
}

export function dashboardSessionPublicPayloadWithUser(session: DashboardSession, user: AuthUser) {
  return {
    ...dashboardSessionPublicPayload(session),
    user,
  }
}

function createDashboardSessionClaims(
  user: AuthUser,
  options: DashboardSessionOptions,
  now: number
): DashboardSessionClaims {
  // Baked in at login, so the lifetime cannot be escalated by a later request.
  const lifetime = options.rememberMe
    ? DASHBOARD_SESSION_REMEMBERED_LIFETIME_MS
    : DASHBOARD_SESSION_LIFETIME_MS
  return {
    user: compactDashboardSessionUser(user),
    createdAt: now,
    lastSeenAt: now,
    expiresAt: now + lifetime,
  }
}

function compactDashboardSessionUser(user: AuthUser): AuthUser {
  const compactUser: AuthUser = {}
  copyStringClaim(compactUser, user, "sub")
  copyStringClaim(compactUser, user, "email")
  copyStringClaim(compactUser, user, "name")
  copyStringClaim(compactUser, user, "username")
  copyStringClaim(compactUser, user, "profileImageUrl")
  if (user.dashboardLanguage === "en" || user.dashboardLanguage === "ja") {
    compactUser.dashboardLanguage = user.dashboardLanguage
  }
  /**
   * Roles live at the top level, not under `user_metadata`.
   *
   * Compaction used to move them into the Auth0 mirror, which is what left the
   * client gates reading metadata: the cookie cannot carry the permission list
   * — an admin's runs past the 4096-byte limit — so the mirror was all they
   * had. Permissions are rehydrated from SQL by the session endpoint instead,
   * and these role keys are kept only for display and the server-side role
   * gate.
   */
  const role = stringClaim(user.role)
  const roles = Array.from(
    new Set(
      [role, ...(Array.isArray(user.roles) ? user.roles : [])].filter(
        (value): value is string => typeof value === "string" && value.length > 0
      )
    )
  )
  if (role) compactUser.role = role
  if (roles.length > 0) compactUser.roles = roles
  return compactUser
}

function copyStringClaim(target: AuthUser, source: AuthUser, key: keyof AuthUser) {
  const value = stringClaim(source[key])
  if (value) target[key] = value
}

function stringClaim(value: unknown) {
  return typeof value === "string" && value.length > 0 ? value : undefined
}

async function signDashboardSession(claims: DashboardSessionClaims) {
  const payload = base64UrlEncode(JSON.stringify(claims))
  const signature = await signPayload(payload)
  return `${payload}.${signature}`
}

async function verifyDashboardSession(token: string): Promise<DashboardSessionClaims | null> {
  try {
    const [payloadValue, signature] = token.split(".")
    if (!payloadValue || !signature || !(await verifyPayloadSignature(payloadValue, signature))) return null
    const payload = JSON.parse(base64UrlDecode(payloadValue)) as Partial<DashboardSessionClaims>
    if (!payload.user || !payload.createdAt || !payload.lastSeenAt || !payload.expiresAt) return null
    return {
      user: payload.user,
      createdAt: payload.createdAt,
      lastSeenAt: payload.lastSeenAt,
      expiresAt: payload.expiresAt,
    }
  } catch {
    return null
  }
}

function sessionSecret() {
  const secret =
    process.env.AUTH_SESSION_SECRET ||
    process.env.AUTH0_CLIENT_SECRET ||
    (allowLocalDevBypass() ? "local-dashboard-session-secret" : "")
  if (!secret) throw new Error("AUTH_SESSION_SECRET is required for dashboard cookie sessions.")
  return secret
}

async function signPayload(payload: string) {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(sessionSecret()),
    { hash: "SHA-256", name: "HMAC" },
    false,
    ["sign"]
  )
  const signature = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(payload))
  return bytesToBase64Url(new Uint8Array(signature))
}

async function verifyPayloadSignature(payload: string, signature: string) {
  const expected = await signPayload(payload)
  if (expected.length !== signature.length) return false
  let difference = 0
  for (let index = 0; index < expected.length; index++) {
    difference |= expected.charCodeAt(index) ^ signature.charCodeAt(index)
  }
  return difference === 0
}

function base64UrlEncode(value: string) {
  return Buffer.from(value, "utf8").toString("base64url")
}

function base64UrlDecode(value: string) {
  return Buffer.from(value, "base64url").toString("utf8")
}

function bytesToBase64Url(bytes: Uint8Array) {
  return Buffer.from(bytes).toString("base64url")
}

function serializeDashboardSessionCookie(token: string, expiresAt: number) {
  return serializeCookie(DASHBOARD_SESSION_COOKIE, token, {
    expires: unixMsDate(expiresAt),
    maxAge: Math.max(0, Math.floor((expiresAt - utcUnixMs()) / 1000)),
    path: "/",
    httpOnly: true,
    secure: shouldUseSecureCookies(),
    sameSite: "Lax",
  })
}

function serializeCookie(
  name: string,
  value: string,
  options: {
    expires?: Date
    httpOnly?: boolean
    maxAge?: number
    path?: string
    sameSite?: "Lax" | "Strict" | "None"
    secure?: boolean
  }
) {
  const parts = [`${name}=${encodeURIComponent(value)}`]
  if (options.maxAge !== undefined) parts.push(`Max-Age=${options.maxAge}`)
  if (options.expires) parts.push(`Expires=${options.expires.toUTCString()}`)
  if (options.path) parts.push(`Path=${options.path}`)
  if (options.httpOnly) parts.push("HttpOnly")
  if (options.secure) parts.push("Secure")
  if (options.sameSite) parts.push(`SameSite=${options.sameSite}`)
  return parts.join("; ")
}

function shouldUseSecureCookies() {
  const stage = process.env.PUBLIC_STAGE || process.env.VITE_PUBLIC_STAGE
  const domain = process.env.DOMAIN || ""
  if (domain.includes("localhost") || domain.includes("127.0.0.1")) return false
  return stage !== "local" && stage !== undefined
}

function allowLocalDevBypass() {
  return (
    isDevStage(process.env.PUBLIC_STAGE) &&
    readBooleanEnv(process.env.DASHBOARD_AUTH_BYPASS) &&
    !process.env.AUTH0_DOMAIN &&
    !process.env.AUTH0_CLIENT_ID
  )
}

function readBooleanEnv(value: string | undefined) {
  if (!value) return false
  return ["1", "true", "yes", "on"].includes(value.toLowerCase())
}
